import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import OdilonLogo from "@/assets/odilon/Odilon_03.svg";
import { normalizePhoneNumber } from "@/lib/phone";

/** Matches the server-side OTP_RESEND_COOLDOWN_SECONDS (a resend inside it silently does nothing). */
const RESEND_COOLDOWN_SECONDS = 60;

export default function Login() {
  const [countryCode, setCountryCode] = useState("+33");
  const [phoneInput, setPhoneInput] = useState("");
  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const otpInputRef = useRef<HTMLInputElement>(null);
  const {
    signInWithPhone,
    verifyOtp,
    resetAuth,
    error,
    loading,
    otpSent,
    phone: contextPhone,
  } = useAuth();

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow users to paste full numbers or type freely
    setPhoneInput(value);
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneInput.trim()) return;

    // Normalize the phone number to E.164 format
    const normalizedPhone = normalizePhoneNumber(phoneInput, countryCode);
    await signInWithPhone(normalizedPhone);
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim() || !contextPhone) return;
    await verifyOtp(contextPhone, otp);
  };

  useEffect(() => {
    if (!otpSent || loading) return;
    const timer = window.setTimeout(() => {
      const input = otpInputRef.current;
      if (input) {
        input.focus({ preventScroll: true });
        // Ensure cursor is at the end on iOS/Safari
        input.setSelectionRange?.(input.value.length, input.value.length);
      }
    }, 50);
    return () => window.clearTimeout(timer);
  }, [otpSent, loading]);

  useEffect(() => {
    if (otpSent || loading) return;
    const timer = window.setTimeout(() => {
      const input = phoneInputRef.current;
      if (input) {
        input.focus({ preventScroll: true });
        const len = input.value.length;
        input.setSelectionRange?.(len, len);
      }
    }, 50);
    return () => window.clearTimeout(timer);
  }, [otpSent, loading]);

  // Restart the resend cooldown each time a code is (re)sent.
  useEffect(() => {
    setResendCooldown(otpSent ? RESEND_COOLDOWN_SECONDS : 0);
  }, [otpSent, resendCount]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(
      () => setResendCooldown((seconds) => seconds - 1),
      1000,
    );
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (!contextPhone || loading || resendCooldown > 0) return;
    setOtp("");
    await signInWithPhone(contextPhone);
    setResendCount((count) => count + 1);
  };

  const resetForm = () => {
    setPhoneInput("");
    setOtp("");
    resetAuth();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-4">
          <div className="flex justify-center mb-4">
            <img src={OdilonLogo} alt="Chauvin Paris" className="h-32 w-auto" />
          </div>
          <h1 className="text-4xl font-semibold tracking-wider font-['Didot',_sans-serif] text-gray-800">
            CHAUVIN PARIS
          </h1>
          <p className="text-muted-foreground text-base">
            {otpSent
              ? "Vérifiez votre téléphone"
              : "Connectez-vous avec votre téléphone"}
          </p>
        </div>

        <div className="bg-card border rounded-lg p-6 space-y-4">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {!otpSent ? (
            <form onSubmit={handlePhoneSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Numéro de téléphone</Label>
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    inputMode="tel"
                    value={countryCode}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Ensure it starts with + and only contains digits after
                      if (
                        value === "" ||
                        value === "+" ||
                        /^\+\d{0,4}$/.test(value)
                      ) {
                        setCountryCode(value);
                      }
                    }}
                    placeholder="+33"
                    disabled={loading}
                    className="w-20"
                    maxLength={5}
                  />
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    ref={phoneInputRef}
                    value={phoneInput}
                    onChange={handlePhoneInputChange}
                    placeholder="6 12 34 56 78"
                    disabled={loading}
                    required
                    className="flex-1"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading || !phoneInput.trim()}
              >
                {loading ? "Envoi..." : "Envoyer le code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">Code de vérification</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  ref={otpInputRef}
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  placeholder="123456"
                  disabled={loading}
                  maxLength={6}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {resendCount > 0
                    ? "Un nouveau code a été envoyé par SMS au "
                    : "Vérifiez vos SMS : un code a été envoyé au "}
                  {contextPhone || "votre téléphone"}
                </p>
              </div>
              <div className="space-y-2">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || !otp.trim() || !contextPhone}
                >
                  {loading ? "Vérification..." : "Vérifier"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleResend}
                  disabled={loading || resendCooldown > 0 || !contextPhone}
                >
                  {resendCooldown > 0
                    ? `Renvoyer le code (${resendCooldown} s)`
                    : "Renvoyer le code"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Changer de numéro
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
